import { motion } from "framer-motion";
import { 
  Youtube, Linkedin, Twitter, Instagram, Facebook, Link2, CheckCircle2, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PLATFORMS = [
  { id: "youtube", name: "YouTube", icon: Youtube, color: "text-red-500", type: "oauth" },
  { id: "linkedin", name: "LinkedIn", icon: Linkedin, color: "text-blue-600", type: "oauth" },
  { id: "twitter", name: "Twitter/X", icon: Twitter, color: "text-sky-500", type: "api_key" },
  { id: "instagram", name: "Instagram", icon: Instagram, color: "text-pink-500", type: "oauth" },
  { id: "facebook", name: "Facebook", icon: Facebook, color: "text-blue-500", type: "oauth" },
  { id: "tiktok", name: "TikTok", icon: TikTokIcon, color: "text-white", type: "oauth" }
];

/**
 * ConnectedPlatforms Component
 * Shows connection status for all social media platforms
 */
export default function ConnectedPlatforms({ 
  connectedPlatforms, 
  connectingPlatform,
  onConnect,
  onDisconnect
}) {
  return (
    <Card className="glass-card border-white/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Link2 className="w-5 h-5 text-primary" />
          Connected Platforms
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {PLATFORMS.map((platform) => {
            const isConnected = connectedPlatforms[platform.id];
            const isConnecting = connectingPlatform === platform.id;
            const Icon = platform.icon;
            
            return (
              <motion.div
                key={platform.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`
                  p-3 rounded-lg border transition-all
                  ${isConnected 
                    ? 'border-green-500/30 bg-green-500/5' 
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                  }
                `}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${platform.color}`} />
                    <span className="text-sm font-medium">{platform.name}</span>
                  </div>
                  {isConnected && (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  )}
                </div>
                
                {isConnected ? (
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs bg-green-500/10 border-green-500/30 text-green-400">
                      {platform.type === 'api_key' ? 'Connected (API Key)' : 'Connected'}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs h-6 px-2 text-muted-foreground hover:text-red-400"
                      onClick={() => onDisconnect(platform.id)}
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-xs h-7"
                    onClick={() => onConnect(platform.id)}
                    disabled={isConnecting}
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </Button>
                )}
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
